import { debounce } from './async'

/**
 * Get the width of the scrollbar on the current browser.
 *
 * @returns {number} The width of the scrollbar in pixels.
 */
export function measureScrollBarWidth() {
	const auxNode = document.createElement('div')
	auxNode.style.cssText = 'overflow:scroll;visibility:hidden;position:absolute;'
	document.body.appendChild(auxNode)

	const width = auxNode.offsetWidth - auxNode.clientWidth
	auxNode.remove()

	return width
}

/**
 * Measure the distance between an HTML element and the document's top, left, right, and bottom edges of the document.
 *
 * @param {HTMLElement} element - The HTML element to measure.
 * @returns {Object} An object containing distance measurements in pixels:
 *   - `left`: The horizontal distance from the element's left edge to the document's left edge.
 *   - `right`: The horizontal distance from the element's right edge to the document's right edge.
 *   - `top`: The vertical distance from the element's top edge to the document's top edge.
 *   - `bottom`: The vertical distance from the element's bottom edge to the document's bottom edge.
 */
export function measureElementDistanceToDocument(element) {
	const MAX_LOOPS = 64
	let loops = 0
	let [left, top] = [0, 0]
	let _element = element

	if (!(element instanceof HTMLElement)) {
		throw new TypeError('[helpers/dom/measureElementDistanceToDocument] Invalid argument: "element" must be an HTML element.')
	}

	while (_element && loops <= MAX_LOOPS) {
		left += _element.offsetLeft
		top += _element.offsetTop
		_element = _element.offsetParent

		loops++

		if (loops >= MAX_LOOPS) {
			throw new Error('[helpers/dom/measureElementDistanceToDocument] Maximum loop count reached while traversing the element hierarchy.')
		}
	}

	return {
		left,
		right: document.body.offsetWidth - (left + element.offsetWidth),
		top,
		bottom: document.body.offsetHeight - (top + element.offsetHeight),
	}
}

/**
 * Creates a reference to an element's bounds.
 *
 * TODO: update
 */
export function createBoundingClientRectRef(element, options) {
	const {
		//
		trigger = element,
		onResize,
		measureDistanceToDocument,
		debounce: debounceDelay = 128,
	} = options || {}

	const resizeObserver = new ResizeObserver(createDebouncedObserverCallback())
	let [x, y, width, height, left, top, documentX, documentY] = [null, null, null, null, null, null]

	function getCurrentContextBounds() {
		return { x, y, width, height, documentX, documentY }
	}

	function onResizeObserver() {
		const previousState = { ...getCurrentContextBounds() }
		const bounds = element.getBoundingClientRect()

		x = bounds.x
		y = bounds.y
		width = bounds.width
		height = bounds.height
		left = element.offsetLeft
		top = element.offsetTop

		if (measureDistanceToDocument) {
			const distanceToDocument = measureElementDistanceToDocument(element)
			documentX = distanceToDocument.left
			documentY = distanceToDocument.top
		}

		const currentState = getCurrentContextBounds()
		const triggerCallback = JSON.stringify(previousState) !== JSON.stringify(currentState)

		if (triggerCallback && typeof onResize === 'function') {
			const args = { x, y, left, top, width, height, element }

			if (measureDistanceToDocument) {
				args.documentX = documentX
				args.documentY = documentY
			}

			setTimeout(() => {
				onResize(args)
			}, 0)
		}
	}

	function createDebouncedObserverCallback() {
		return debounce(onResizeObserver, debounceDelay, true)
	}

	function observe(immediateUpdate) {
		if (immediateUpdate) {
			onResizeObserver()
		}

		resizeObserver.observe(trigger)

		return this
	}

	function unobserve() {
		resizeObserver.unobserve(trigger)
		return this
	}

	function destroy() {
		resizeObserver.disconnect()
	}

	return {
		get element() {
			return element
		},
		get x() {
			return x
		},
		get y() {
			return y
		},
		get width() {
			return width
		},
		get height() {
			return height
		},
		get left() {
			return left
		},
		get top() {
			return top
		},
		get documentX() {
			return documentX
		},
		get documentY() {
			return documentY
		},
		observe,
		unobserve,
		destroy,
	}
}

/**
 * Asynchronous utility function that resolves a Promise when a specified condition is met for a given DOM element,
 * or when a timeout is reached. It uses a MutationObserver to detect changes in the DOM structure.
 *
 * @param {Object} options - Configuration options for the function.
 * @param {HTMLElement} options.element - The DOM element to observe.
 * @param {Function} options.condition - The condition function that determines when to resolve the Promise.
 *                                      Should return a truthy value when the condition is met.
 * @param {boolean} [options.subtree=false] - A boolean indicating whether to observe the entire subtree of the element.
 * @param {number} [options.timeout=2000] - The maximum time to wait for the condition to be met, in milliseconds.
 *
 * @returns {Promise} A Promise that resolves when either the condition is met or the timeout is reached.
 */
export function promisifyElementTreeCondition({ element, condition, subtree = false, timeout = 2000 }) {
	return new Promise((resolve, reject) => {
		try {
			const resolveTimeout = setTimeout(resolve, timeout)

			if (condition(element)) {
				clearTimeout(resolveTimeout)
				return resolve()
			}

			const mutationObserver = new MutationObserver(onMutation)
			mutationObserver.observe(element, { childList: true, subtree })

			function onMutation(mutations) {
				for (let index = 0; index < mutations.length; index++) {
					if (condition(element)) {
						mutationObserver.disconnect()
						clearTimeout(resolveTimeout)
						resolve()
					}
				}
			}
		} catch (e) {
			console.error('[helpers/nuxt/dynamicTemplateLoaded]', e)
			reject(e)
		}
	})
}

/**
 * Returns a root tree structure of DOM elements between a child and a specified parent with a depth limit.
 *
 * @param {HTMLElement} child - The child element to start building the tree from.
 * @param {HTMLElement} parent - The parent element at which the tree building should stop.
 * @param {Object} [options] - Additional options for customizing the behavior.
 * @param {number} [options.maxDepth=32] - The maximum depth limit for building the tree.
 * @param {boolean} [options.includeProvidedNodes=false] - Whether to include the provided child and parent nodes in the final tree.
 * @returns {Array<HTMLElement>} - An array representing the tree of DOM elements from the child's parent to the specified parent.
 */
export function getRootTreeBetweenNodes(child, parent, options) {
	const maxDepth = options?.maxDepth || 32
	const includeProvidedNodes = options?.includeProvidedNodes ?? false

	let tree = []
	let depth = 0
	let currentNode = child.parentElement

	while (currentNode.parentElement !== parent && depth <= maxDepth) {
		tree.push(currentNode)

		currentNode = currentNode?.parentElement

		depth++
	}

	if (includeProvidedNodes) {
		tree = [child, ...tree, parent]
	}

	return tree
}

/**
 * Promisifies the loading process for an HTMLImageElement or HTMLVideoElement.
 *
 * @param {HTMLImageElement|HTMLVideoElement} element - The media element.
 * @param {number} [maxTimeout=10000] - Maximum time to wait for the image to load (in milliseconds).
 * @returns {Promise} - A Promise that resolves when the video is loaded successfully or if the specified timeout occurs.
 */
export function promisifyMediaLoad(element, maxTimeout = 10000) {
	const type = element?.nodeName

	if (!['IMG', 'VIDEO'].includes(type)) {
		console.error(`[helpers/dom/promisifyMediaLoad] Invalid node type on element (${type}) — expects 'IMG' or 'VIDEO'.`)
		return new Promise((resolve) => resolve())
	}

	return new Promise((resolve) => {
		let timeout = null

		if (
			(type === 'IMG' && element.complete && element.naturalHeight !== undefined && element.naturalHeight !== 0) || //
			(element.type === 'VIDEO' && element.readyState >= 3)
		) {
			resolve()
		}

		if (maxTimeout && typeof maxTimeout === 'number' && maxTimeout > 0) {
			timeout = setTimeout(resolve, maxTimeout)
		}

		function onLoaded() {
			resolve()

			if (timeout) {
				clearTimeout(timeout)
			}
		}

		if (type === 'IMG') {
			element.addEventListener('load', onLoaded, { once: true })
			element.addEventListener('error', onLoaded, { once: true })
		} else if (type === 'VIDEO') {
			element.addEventListener('loadeddata', onLoaded, { once: true })
		}
	})
}

export function createIntersectionObserver(elements, callback, options = {}) {
	const { once } = options

	let instance = new IntersectionObserver(([entry]) => {
		callback(entry)

		if (once && entry.isIntersecting) {
			destroy()
		}
	}, options)

	;[elements].flat().forEach((element) => {
		instance.observe(element)
	})

	function destroy() {
		if (instance) {
			instance.disconnect()
			instance = null
		}
	}

	return { destroy }
}
